import React, { Fragment } from 'react';
import { graphql, compose } from 'react-apollo';
import {
  assignmentReview,
  assignmentPreviewDataOnly,
} from 'legacy-src/queries';
import { learnosityReportByUsers } from 'legacy-src/utility/progressHelpers';
import get from 'lodash/get';

import PerformanceItem from './index';
import PageTitle from 'legacy/src/components/PageTitle';
import {
  isValidJSONString,
  parseNonEmptyJSONObject,
} from 'legacy-src/utility/utils';
import withSubmissionData from 'legacy-src/utility/withSubmissionData';

/**
 * Wrapper for <PerformanceItem /> component. Loads data for students.
 * */
const StudentWrapper = (props) => {
  const {
    params = {},
    questions = [],
    studentResponses = {},
    submissionData = [],
    meQuery: { me },
  } = props;
  const question = questions.find((q) => q.id === params.questionId) || {};
  const part = (question.children || []).find((c) => c.id === params.partId);
  const title = (part && part.title) || '';
  const { isStudent, initId } = me;

  if (questions.length === 0) {
    return null;
  }

  //removes student scores that not fully scored
  const getCompletedStudentResponses = () => {
    const completedStudentResponses = {};
    if (isStudent) {
      completedStudentResponses[initId] = studentResponses[initId];
    } else {
      submissionData.forEach((submission) => {
        if (
          [
            'scored',
            'complete',
            'student_scored',
            'teacher_and_student_scored',
          ].includes(submission.status)
        ) {
          completedStudentResponses[submission.user_id] =
            studentResponses[submission.user_id];
        }
      });
    }
    return completedStudentResponses;
  };

  const completedStudentResponses = getCompletedStudentResponses();
  return (
    <Fragment>
      <PageTitle titleString={title} />
      <PerformanceItem
        {...props}
        studentResponses={completedStudentResponses}
        isStudent={isStudent}
        showTags
      />
    </Fragment>
  );
};

StudentWrapper.displayName = 'StudentWrapper';

const options = (optionsType) => ({
  options: (props) => {
    const {
      assignment,
      params: { assignmentId, ppcPartId, studentId },
      tagPairsData: {
        assignmentProgressWithTagPairs: { otherParts },
      },
      meQuery: { me },
      hasPartAssignment,
      teacherOtherParts,
      isPPC,
      learnosityReportsData,
    } = props;
    const currentPpcPartId = Number.parseInt(ppcPartId, 10) || 0;
    let currentStudentId;

    const isOtherPart =
      currentPpcPartId &&
      currentPpcPartId !== Number.parseInt(assignmentId, 10);

    let skip = false;

    let currentAssignment;
    if (isOtherPart && isPPC) {
      currentAssignment = (
        hasPartAssignment ? otherParts : teacherOtherParts
      ).find(({ assignmentId }) => assignmentId === currentPpcPartId);
    } else {
      currentAssignment = {
        ...assignment,
        assignmentId: assignment.id,
      };
    }

    //check if user has session, could be just locked, but with user results
    const { assignmentId: currentAssignmentId, studentSessions } =
      currentAssignment || {};
    const studentScoredSessions = (studentSessions || []).filter(
      ({ scoringCompletedAt }) => !!scoringCompletedAt,
    );
    const userHasSession = !!studentScoredSessions.find(
      ({ userId }) => userId === studentId,
    );
    const { isStudent: isCurrentUserStudent, initId } = me;

    if (isCurrentUserStudent) {
      /**
       * Support for QB
       * */
      const reportByStudents = learnosityReportByUsers(learnosityReportsData);

      /**
       * Support for student user in QB
       * */
      if (reportByStudents[studentId] || isCurrentUserStudent) {
        skip = optionsType === 'assignment_preview';
        currentStudentId = studentId || initId;
      }
    } else {
      const notLockedAssignment =
        currentAssignment &&
        currentAssignment.status &&
        currentAssignment.status !== 'locked';
      if (
        userHasSession &&
        (!isOtherPart || (isOtherPart && notLockedAssignment))
      ) {
        currentStudentId = studentId;
        skip = optionsType === 'assignment_preview';
      } else {
        skip = optionsType === 'assignment_review';
      }
    }

    const variables = {
      scorerType: 'student',
      config: JSON.stringify({}),
      inline: true,
      showCorrectAnswers: true,
      assignmentId: currentAssignmentId,
      studentId: currentStudentId,
      assignmentIdAsInt: parseInt(currentAssignmentId, 10),
    };

    return {
      skip,
      variables,
      fetchPolicy: 'network-only',
    };
  },

  props: ({
    data: {
      assignmentReview: assignmentReviewData,
      assignmentPreview: assignmentPreviewData,
      error,
      assignment,
    },
  }) => {
    if (error) {
      return {
        error: error.message,
      };
    }

    const data = assignmentReviewData || assignmentPreviewData;

    if (data) {
      const { learnositySignedRequest, scoringRubric } = data;
      const scoringRubricResponseAnalysis =
        assignment && assignment.scoringRubricResponseAnalysis;

      return {
        studentResponses: parseNonEmptyJSONObject(assignment.studentResponses),
        scoringRubricResponses: parseNonEmptyJSONObject(
          assignment.scoringRubricResponses,
        ),
        progressPerStudent: assignment.progress,
        learnosityServerData: JSON.parse(learnositySignedRequest),
        scoringRubricResponseAnalysis:
          isValidJSONString(scoringRubricResponseAnalysis) &&
          JSON.parse(scoringRubricResponseAnalysis),
        scoringRubric: assignmentReviewData ? scoringRubric : null,
        removeMainScoreLabel:
          get(assignment, 'progress.studentScored') &&
          !get(assignment, 'progress.teacherAndStudentScored'),
      };
    }
  },
});

const withAssignmentReview = graphql(
  assignmentReview,
  options('assignment_review'),
);
const withAssignmentPreview = graphql(
  assignmentPreviewDataOnly,
  options('assignment_preview'),
);

StudentWrapper.defaultProps = {
  tagPairsData: {
    assignmentProgressWithTagPairs: {
      assessmentShortName: '',
      otherParts: [],
    },
  },
};

export default compose(
  withAssignmentReview,
  withAssignmentPreview,
  withSubmissionData,
)(StudentWrapper);
